import { useState, type FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiClient, ApiError } from "../lib/api";

/**
 * The organization-name form + create mutation, shared by the first-run
 * `/welcome` screen and the "create another organization" screen
 * (`/organizations/new`). The backend permits an operator who already owns
 * providers to create more (RBAC allows it; the team_member model is
 * one-to-many), so both surfaces reuse this exact submit path.
 *
 * On success the server has already set the new provider as active; we
 * invalidate the session/onboarding/my-providers caches and hand control back
 * to the caller via `onSuccess` (each surface routes differently — first-run
 * and additional-org both go into the onboarding wizard).
 */
export function CreateOrgForm({
  onSuccess,
  submitLabel = "Create organization",
}: {
  onSuccess: () => void;
  submitLabel?: string;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: (orgName: string) => apiClient.providers.create({ name: orgName }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["session"] });
      queryClient.invalidateQueries({ queryKey: ["onboarding"] });
      queryClient.invalidateQueries({ queryKey: ["team", "my-providers"] });
      onSuccess();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError(err instanceof Error ? err.message : "Failed to create organization");
      }
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Name is required");
      return;
    }
    if (trimmed.length > 255) {
      setError("Name must be 255 characters or fewer");
      return;
    }
    setError(null);
    createMutation.mutate(trimmed);
  }

  const submitting = createMutation.isPending;

  return (
    <>
      <form onSubmit={onSubmit} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <label
          htmlFor="org-name"
          style={{ fontSize: 12, fontWeight: 500, color: "var(--ad-fg-2)" }}
        >
          Organization name
        </label>
        <input
          id="org-name"
          type="text"
          required
          maxLength={255}
          placeholder="Acme Inc."
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={submitting}
          autoFocus
          style={{
            padding: "10px 12px",
            border: "1px solid var(--ad-hairline)",
            borderRadius: 8,
            fontSize: 14,
            background: "var(--ad-surface-2)",
            color: "var(--ad-fg-1)",
          }}
        />
        <button
          type="submit"
          className="ad-btn ad-btn-primary"
          disabled={submitting || !name.trim()}
          style={{ width: "100%", justifyContent: "center", marginTop: 4 }}
        >
          {submitting ? "Creating…" : submitLabel}
        </button>
      </form>

      {error && (
        <div
          style={{
            marginTop: 16,
            padding: 10,
            borderRadius: 8,
            fontSize: 13,
            background: "rgba(220, 38, 38, 0.08)",
            color: "#b91c1c",
          }}
        >
          {error}
        </div>
      )}
    </>
  );
}
